import api from '@seller-hermes/utils/api';
import { ACTIVE_SELLER_GROUP_HEADER } from './portal-local-context';
import type { PortalProfile } from './portal.types';
import type {
  AccountSellerGroupRow,
  AccountUserSearchRow,
  InviteUserInput,
  AccountUsersListResult,
  PaginatedData,
  PortalManagedUser,
  PortalRole,
  PortalSeller,
  PortalUserDetail,
  UpdateUserLinkInput,
  UpdateUserV2Input,
} from './portal-users.types';

const PORTAL_PROFILES: PortalProfile[] = ['LOG', 'GO', 'PAY'];

const toProfile = (value: string | null): PortalProfile =>
  PORTAL_PROFILES.includes(value as PortalProfile)
    ? (value as PortalProfile)
    : 'LOG';

// Per-request active seller-group header. The portal has no globally-active
// seller group (the home screen is where the user selects one), so each
// user-management request carries the seller group it operates on explicitly.
const withSellerGroup = (
  sellerGroupId?: string,
  config: Record<string, unknown> = {},
) =>
  sellerGroupId
    ? {
        ...config,
        headers: {
          ...((config.headers as Record<string, string>) || {}),
          [ACTIVE_SELLER_GROUP_HEADER]: sellerGroupId,
        },
      }
    : config;

const RELATIVE_DIVISIONS: Array<{ amount: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { amount: 60, unit: 'seconds' },
  { amount: 60, unit: 'minutes' },
  { amount: 24, unit: 'hours' },
  { amount: 7, unit: 'days' },
  { amount: 4.34524, unit: 'weeks' },
  { amount: 12, unit: 'months' },
  { amount: Number.POSITIVE_INFINITY, unit: 'years' },
];

const relativeTimeFormatter =
  typeof Intl !== 'undefined' && 'RelativeTimeFormat' in Intl
    ? new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' })
    : null;

export const formatRelativeAccess = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  if (!relativeTimeFormatter) return date.toLocaleDateString('pt-BR');

  let duration = (date.getTime() - Date.now()) / 1000;
  for (const division of RELATIVE_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return relativeTimeFormatter.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return date.toLocaleDateString('pt-BR');
};

export const toManagedUser = (row: AccountUserSearchRow): PortalManagedUser => {
  const displayName = row.name || row.email;
  return {
    id: row.user_id,
    name: displayName,
    email: row.email,
    initial: (displayName.trim()[0] || '?').toUpperCase(),
    type: row.type,
    operations: row.operations.map(toProfile),
    status: row.active ? 'Ativo' : 'Inativo',
    lastAccess: formatRelativeAccess(row.last_accessed_at),
    links: row.seller_groups.map((link) => ({
      id: link.id,
      operationName: link.name ?? '',
      operationProfile: toProfile(link.profile),
      type: link.type,
      role: link.role?.name ?? '',
      roleId: link.role?.id ?? null,
      branches: [],
    })),
  };
};

export class PortalUsersService {
  static async listUsers(
    params: {
      from?: number;
      size?: number;
      search?: string;
      type?: string;
      operation?: string;
    },
    sellerGroupId?: string,
  ): Promise<AccountUsersListResult> {
    const res = await api.get<AccountUsersListResult>(
      '/users',
      withSellerGroup(sellerGroupId, { params }),
    );
    return res.data;
  }

  static async listAccountSellerGroups(): Promise<AccountSellerGroupRow[]> {
    const res = await api.get<AccountSellerGroupRow[]>('/account/seller-groups');
    return res.data;
  }

  static async inviteUser(
    body: InviteUserInput,
    sellerGroupId?: string,
  ): Promise<void> {
    await api.post('/v2/users/invite', body, withSellerGroup(sellerGroupId));
  }

  static async updateUserLinks(
    userId: string,
    body: { add?: UpdateUserLinkInput[]; remove?: string[] },
    sellerGroupId?: string,
  ): Promise<void> {
    await api.patch(
      `/v2/users/${userId}/links`,
      body,
      withSellerGroup(sellerGroupId),
    );
  }

  static async setUserActive(
    userId: string,
    active: boolean,
    sellerGroupId?: string,
  ): Promise<void> {
    await api.patch(
      `/v2/users/${userId}/active`,
      { active },
      withSellerGroup(sellerGroupId),
    );
  }

  static async getUserDetail(
    userId: string,
    sellerGroupId: string,
  ): Promise<PortalUserDetail> {
    const res = await api.get<PortalUserDetail>(
      `/users-page/v1/user/${userId}`,
      withSellerGroup(sellerGroupId),
    );
    return res.data;
  }

  // Filiais que o usuário logado pode CONCEDER nesta operação: um
  // Proprietário concede qualquer filial da operação, inclusive as que ele
  // próprio não acessa (diferente de /v2/sellers/lookup, que é escopado ao
  // próprio requester e paginado — não usar aqui).
  static async listGrantableSellers(
    sellerGroupId: string,
  ): Promise<PortalSeller[]> {
    const res = await api.get<PortalSeller[]>(
      '/v2/sellers/grantable',
      withSellerGroup(sellerGroupId),
    );
    return res.data.filter((seller) => seller.active);
  }

  static async searchRoles(sellerGroupId: string): Promise<PortalRole[]> {
    const res = await api.post<PaginatedData<PortalRole[]>>(
      '/v4/search/roles',
      {},
      withSellerGroup(sellerGroupId),
    );
    return res.data.data;
  }

  static async updateUserV2(
    userId: string,
    body: UpdateUserV2Input,
    sellerGroupId: string,
  ): Promise<void> {
    await api.patch(
      `/v2/users/${userId}`,
      body,
      withSellerGroup(sellerGroupId),
    );
  }

  // O endpoint autoriza pelos vínculos (requester administra alguma operação
  // do alvo); o header só satisfaz o gate grosso de tipo do guard.
  static async changeUserPassword(
    userId: string,
    newPassword: string,
    sellerGroupId?: string,
  ): Promise<void> {
    await api.patch(
      `/v2/users/${userId}/password`,
      { new_password: newPassword },
      withSellerGroup(sellerGroupId),
    );
  }
}
